import React, { useMemo, useCallback, useEffect } from 'react';
import { Mic, MicOff, PhoneOff, Brain, Waves, Loader2, Keyboard } from 'lucide-react';
import clsx from 'clsx';

const CallInterface = React.memo(({
  character,
  isActive,
  isRecording,
  isAiThinking,
  isReconnecting = false,
  isProcessingMessage,
  isFinalizingSpeech,
  currentMessage,
  duration,
  recordingMode,
  onRecordingModeChange,
  onStartRecording,
  onStopRecording,
  onToggleRecording,
  onEndCall,
}) => {
  const isHoldMode = recordingMode === 'hold';
  const isSpeechBusy = isReconnecting || isAiThinking || isProcessingMessage || isFinalizingSpeech;
  const isModeSwitchDisabled = isRecording || isSpeechBusy;

  // Memoize duration formatting function
  const formatDuration = useCallback((ms) => {
    const seconds = Math.floor(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }, []);

  // Memoize character display name
  const characterName = useMemo(() => {
    return character?.name || 'AI Собеседник';
  }, [character?.name]);

  // Memoize character position
  const characterPosition = useMemo(() => {
    return character?.position || (character?.role === 'secretary' ? 'Секретарь' : 'Директор');
  }, [character?.position, character?.role]);

  // Memoize character company
  const characterCompany = useMemo(() => {
    return character?.company || 'Компания';
  }, [character?.company]);

  // Memoize character emoji
  const characterEmoji = useMemo(() => {
    return character?.role === 'secretary' ? '👩‍💼' : '👨‍💼';
  }, [character?.role]);

  // Memoize state text
  const stateText = useMemo(() => {
    if (isReconnecting) return 'Восстанавливаем соединение...';
    if (isAiThinking) return 'ИИ думает и готовит ответ...';
    if (isRecording) return 'Идет запись вашей речи...';
    if (isProcessingMessage) return 'Обработка ответа ИИ...';
    if (isFinalizingSpeech) return 'Речь отправляется на распознавание...';
    return isHoldMode ? 'Зажмите микрофон или пробел' : 'Нажмите на микрофон для начала';
  }, [isReconnecting, isAiThinking, isRecording, isProcessingMessage, isFinalizingSpeech, isHoldMode]);

  // Memoize state message text
  const stateMessageText = useMemo(() => {
    if (isAiThinking) return 'ИИ обрабатывает вашу речь...';
    if (isRecording) return isHoldMode ? 'Говорите, пока держите кнопку. Отпустите, когда закончите.' : 'Говорите четко, я вас слушаю...';
    if (isProcessingMessage) return 'Почти готово...';
    if (isFinalizingSpeech) return 'Подождите, отправляем фразу собеседнику...';
    return isHoldMode ? 'Держите кнопку микрофона мышкой или клавишу Пробел на клавиатуре' : 'Нажмите на микрофон, чтобы начать диалог';
  }, [isAiThinking, isRecording, isProcessingMessage, isFinalizingSpeech, isHoldMode]);

  // Memoize button title
  const micButtonTitle = useMemo(() => {
    if (isAiThinking) return 'ИИ отвечает, ожидайте...';
    if (isProcessingMessage) return 'ИИ отвечает, ожидайте...';
    if (isFinalizingSpeech) return 'Речь отправляется, подождите...';
    if (isRecording) return isHoldMode ? 'Отпустите кнопку мыши или клавишу Пробел, чтобы отправить речь' : 'Нажмите, чтобы временно выключить микрофон';
    if (isActive) return isHoldMode ? 'Зажмите кнопку мышью или удерживайте Пробел, чтобы говорить' : 'Нажмите для начала записи';
    return 'Сначала начните сессию';
  }, [isAiThinking, isProcessingMessage, isFinalizingSpeech, isRecording, isActive, isHoldMode]);

  // Memoize end call button title
  const endCallTitle = 'Завершить звонок';

  // Memoize progress text
  const progressText = useMemo(() => {
    if (isFinalizingSpeech) return 'Распознает речь...';
    return isAiThinking ? 'Анализирует ситуацию...' : 'Формирует ответ...';
  }, [isAiThinking, isFinalizingSpeech]);

  const modeHelpText = useMemo(() => {
    return isHoldMode
      ? 'Режим удержания: речь записывается только пока зажата кнопка микрофона или клавиша Пробел.'
      : 'Автоматический режим: микрофон сам включается после ответа ИИ. Кнопкой можно временно включить или выключить запись.';
  }, [isHoldMode]);

  // Memoize waveform bars data
  const waveformBars = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      id: i,
      height: `${20 + Math.random() * 80}%`,
      delay: `${i * 0.05}s`,
    }));
  }, []);

  // Memoize current message display
  const displayMessage = useMemo(() => {
    if (currentMessage) return currentMessage;
    return stateMessageText;
  }, [currentMessage, stateMessageText]);

  // Memoize button classes
  const micButtonClasses = useMemo(() => {
    return clsx(
      'relative z-10 w-16 h-16 rounded-full text-white flex items-center justify-center shadow-xl transition-all duration-300 disabled:cursor-not-allowed',
      'hover:shadow-2xl active:scale-95 transform',
      isRecording
        ? 'bg-blue-500 hover:bg-blue-600'
        : isAiThinking
        ? 'bg-red-400 cursor-not-allowed'
        : isProcessingMessage || isFinalizingSpeech
        ? 'bg-red-400 cursor-not-allowed'
        : isActive
        ? 'bg-red-500 hover:bg-red-600 hover:scale-105'
        : 'bg-gray-300 cursor-not-allowed'
    );
  }, [isRecording, isAiThinking, isProcessingMessage, isFinalizingSpeech, isActive]);

  const endCallButtonClasses = useMemo(() => {
    return clsx(
      'w-16 h-16 rounded-full text-white flex items-center justify-center shadow-lg transition-all duration-300 disabled:cursor-not-allowed',
      'hover:shadow-xl active:scale-95 transform',
      !isActive
        ? 'bg-gray-400 cursor-not-allowed'
        : 'bg-red-500 hover:bg-red-600 hover:scale-105'
    );
  }, [isActive]);

  // Memoize visualization area classes
  const visualizationAreaClasses = useMemo(() => {
    return clsx(
      'flex-1 min-h-0 flex flex-col items-center justify-center p-8 overflow-hidden transition-all duration-500',
      isAiThinking || isProcessingMessage || isFinalizingSpeech
        ? 'bg-gradient-to-b from-purple-50 to-orange-50'
        : 'bg-gradient-to-b from-white to-slate-50'
    );
  }, [isAiThinking, isProcessingMessage, isFinalizingSpeech]);

  // Memoize controls area classes
  const controlsAreaClasses = useMemo(() => {
    return clsx(
      'px-6 py-5 border-t transition-all duration-500 flex justify-center items-center gap-6 shrink-0',
      isAiThinking || isProcessingMessage || isFinalizingSpeech
        ? 'border-purple-200 bg-purple-50'
        : 'border-slate-100 bg-white'
    );
  }, [isAiThinking, isProcessingMessage, isFinalizingSpeech]);

  // Memoize state indicator classes
  const stateIndicatorClasses = useMemo(() => {
    return clsx(
      'inline-flex items-center gap-3 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300',
      isAiThinking
        ? 'bg-purple-100 text-purple-700 border-2 border-purple-200'
        : isRecording
        ? 'bg-red-100 text-red-700 border-2 border-red-200'
        : isProcessingMessage || isFinalizingSpeech
        ? 'bg-orange-100 text-orange-700 border-2 border-orange-200'
        : 'bg-blue-100 text-blue-700 border-2 border-blue-200'
    );
  }, [isAiThinking, isRecording, isProcessingMessage, isFinalizingSpeech]);

  // Memoize status ring classes
  const statusRingClasses = useMemo(() => {
    if (!isRecording && !isAiThinking && !isProcessingMessage && !isFinalizingSpeech) return '';
    
    return clsx(
      'absolute inset-0 rounded-full pointer-events-none',
      isRecording && 'recording-pulse bg-red-500',
      isAiThinking && 'thinking-pulse bg-purple-500',
      (isProcessingMessage || isFinalizingSpeech) && 'processing-pulse bg-orange-500'
    );
  }, [isRecording, isAiThinking, isProcessingMessage, isFinalizingSpeech]);

  // Memoize handlers to prevent unnecessary re-renders
  const handleToggleRecording = useCallback(() => {
    if (!isHoldMode) {
      onToggleRecording();
    }
  }, [isHoldMode, onToggleRecording]);

  const handlePointerDown = useCallback((event) => {
    if (!isHoldMode || isSpeechBusy || event.button > 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    onStartRecording();
  }, [isHoldMode, isSpeechBusy, onStartRecording]);

  const handlePointerUp = useCallback((event) => {
    if (!isHoldMode) return;
    event.preventDefault();
    onStopRecording();
  }, [isHoldMode, onStopRecording]);

  const handlePointerCancel = useCallback(() => {
    if (isHoldMode) {
      onStopRecording();
    }
  }, [isHoldMode, onStopRecording]);

  const handleEndCall = useCallback(() => {
    onEndCall();
  }, [onEndCall]);

  useEffect(() => {
    if (!isHoldMode) return undefined;

    const isTypingTarget = (target) => {
      const tagName = target?.tagName?.toLowerCase();
      const isFormField = tagName === 'input' || tagName === 'textarea' || tagName === 'select' || target?.isContentEditable;
      const isOtherButton = tagName === 'button' && target?.dataset?.holdRecording !== 'true';
      return isFormField || isOtherButton;
    };

    const handleKeyDown = (event) => {
      if (event.code !== 'Space' || event.repeat || isSpeechBusy || isTypingTarget(event.target)) return;
      event.preventDefault();
      onStartRecording();
    };

    const handleKeyUp = (event) => {
      if (event.code !== 'Space' || isTypingTarget(event.target)) return;
      event.preventDefault();
      onStopRecording();
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isHoldMode, isSpeechBusy, onStartRecording, onStopRecording]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col flex-1 min-h-0 relative overflow-hidden">
      {/* Call Header */}
      <div className="p-6 border-b border-slate-100 flex justify-between items-start shrink-0">
        <div className="flex gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-white shadow-md flex items-center justify-center text-2xl">
              {characterEmoji}
            </div>
            {isActive && (
              <div className="absolute bottom-0 right-0 w-5 h-5 bg-green-500 border-2 border-white rounded-full"></div>
            )}
          </div>
          <div>
            <h2 className="text-lg font-medium text-slate-900 tracking-tight">
              {characterName}
            </h2>
            <p className="text-sm text-slate-500">
              {characterPosition}, {characterCompany}
            </p>
            <div className="mt-2 flex items-center gap-2">
              {character?.traits?.map((trait, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-md"
                >
                  {trait}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-mono text-slate-900 tabular-nums">
            {formatDuration(duration)}
          </div>
          <div className="text-xs text-slate-400">Длительность</div>
        </div>
      </div>

      {/* Visualization Area */}
      <div className={visualizationAreaClasses}>
        {/* State Indicator with Icons */}
        <div className="mb-8 text-center">
          <div className={stateIndicatorClasses}>
            {/* State Icon */}
            <span className={clsx(
              isAiThinking && 'brain-thinking',
              isRecording && 'wave-recording'
            )}>
              {isAiThinking ? (
                <Brain className="w-4 h-4" />
              ) : isRecording ? (
                <Waves className="w-4 h-4" />
              ) : isProcessingMessage || isFinalizingSpeech ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
            </span>
            
            {/* State Text */}
            <span className="font-medium">
              {stateText}
            </span>
          </div>
        </div>

        {/* Progress Indicator for AI Thinking/Processing */}
        {(isAiThinking || isProcessingMessage || isFinalizingSpeech) && (
          <div className="w-full max-w-md mb-6">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={clsx(
                  'h-full w-1/3 rounded-full',
                  isAiThinking ? 'bg-purple-500' : 'bg-orange-500'
                )}
              >
                <div className="h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-30 progress-indeterminate"></div>
              </div>
            </div>
            <p className="text-center text-xs text-gray-500 mt-2">
              {progressText}
            </p>
          </div>
        )}

        {/* Enhanced Audio Waveform */}
        <div className="h-32 flex items-center gap-1.5 mb-6">
          {waveformBars.map((bar) => (
            <div
              key={bar.id}
              className={clsx(
                'w-1.5 rounded-full transition-all duration-200',
                isRecording || isAiThinking || isProcessingMessage || isFinalizingSpeech
                  ? isAiThinking
                    ? 'bg-purple-500 sound-wave-bar'
                    : isProcessingMessage || isFinalizingSpeech
                    ? 'bg-orange-500 sound-wave-bar'
                    : 'bg-red-500 sound-wave-bar'
                  : 'bg-slate-300'
              )}
              style={{
                height: isRecording || isAiThinking || isProcessingMessage || isFinalizingSpeech ? 'var(--h)' : '20%',
                '--h': bar.height,
                animationDelay: bar.delay,
              }}
            ></div>
          ))}
        </div>

        {/* Current Message with Enhanced Styling */}
        <div className="text-center max-w-lg w-full">
          <p className="text-lg text-slate-700 font-medium text-center leading-relaxed tracking-tight">
            {displayMessage}
          </p>
        </div>
      </div>

      {/* Controls with Enhanced Visual Feedback */}
      <div className={controlsAreaClasses}>
        <div className="w-full max-w-xl flex flex-col items-center gap-3">
          <div
            className="w-full flex flex-col gap-2"
            role="group"
            aria-label="Режим записи речи"
            title="Выберите, как включать микрофон во время разговора"
          >
            <div className="flex items-center justify-center gap-2 text-sm font-medium text-slate-700">
              <Mic className="w-4 h-4 text-slate-500" />
              <span>Режим микрофона</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => onRecordingModeChange('auto')}
                disabled={isModeSwitchDisabled}
                className={clsx(
                  'min-h-[40px] rounded-lg border px-3 py-2 text-left transition-colors',
                  !isHoldMode
                    ? 'border-blue-300 bg-blue-50 text-blue-800'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                  isModeSwitchDisabled && 'cursor-not-allowed opacity-70'
                )}
                aria-pressed={!isHoldMode}
                title={isModeSwitchDisabled ? 'Режим можно поменять после завершения записи или ответа ИИ' : 'Автоматический режим: микрофон сам включается после ответа ИИ. Кнопкой можно временно включить или выключить запись'}
              >
                <span className="block text-sm font-medium">Автоматически</span>
                <span className="block text-xs leading-snug text-slate-500">Сам включается после ответа ИИ</span>
              </button>
              <button
                type="button"
                onClick={() => onRecordingModeChange('hold')}
                disabled={isModeSwitchDisabled}
                className={clsx(
                  'min-h-[40px] rounded-lg border px-3 py-2 text-left transition-colors',
                  isHoldMode
                    ? 'border-blue-300 bg-blue-50 text-blue-800'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                  isModeSwitchDisabled && 'cursor-not-allowed opacity-70'
                )}
                aria-pressed={isHoldMode}
                title={isModeSwitchDisabled ? 'Режим можно поменять после завершения записи или ответа ИИ' : 'Режим удержания: держите кнопку микрофона мышкой или клавишу Пробел, пока говорите'}
              >
                <span className="block text-sm font-medium">Удерживать</span>
                <span className="block text-xs leading-snug text-slate-500">Держите мышкой или клавишей Пробел</span>
              </button>
            </div>
            <p className="text-center text-xs text-slate-500" title={modeHelpText}>
              {modeHelpText}
            </p>
          </div>

          <div className="flex justify-center items-start gap-8">
            {/* Microphone Button with Enhanced States */}
            <div className="flex flex-col items-center gap-2">
              <div className="relative w-16 h-16">
                <div className={statusRingClasses}></div>
                <button
                  onClick={handleToggleRecording}
                  onPointerDown={handlePointerDown}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerCancel}
                  onContextMenu={(event) => event.preventDefault()}
                  disabled={!isActive || isSpeechBusy}
                  className={micButtonClasses}
                  title={micButtonTitle}
                  aria-label={micButtonTitle}
                  data-hold-recording="true"
                >
                  <span className={clsx(
                    isRecording && 'animate-pulse'
                  )}>
                    {isRecording ? (
                      <MicOff className="w-7 h-7" />
                    ) : (
                      <Mic className={clsx(
                        'w-7 h-7',
                        isSpeechBusy && 'text-gray-300'
                      )} />
                    )}
                  </span>
                </button>
              </div>
              <div className="text-center">
                <div className="text-xs font-medium text-slate-700">Микрофон</div>
                <div className="text-[11px] text-slate-500">
                  {isHoldMode ? 'Держите, чтобы говорить' : 'Включается сам'}
                </div>
              </div>
            </div>

            {/* End Call Button */}
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={handleEndCall}
                disabled={!isActive}
                className={endCallButtonClasses}
                title={endCallTitle}
                aria-label={endCallTitle}
              >
                <PhoneOff className="w-7 h-7" />
              </button>
              <div className="text-center">
                <div className="text-xs font-medium text-slate-700">Завершить</div>
                <div className="text-[11px] text-slate-500">Закончить звонок</div>
              </div>
            </div>
          </div>

          {isHoldMode && (
            <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" title="Клавиатура: удерживайте Пробел для записи речи">
              <Keyboard className="w-4 h-4 text-slate-500" />
              <span>Клавиатура: удерживайте Пробел</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

CallInterface.displayName = 'CallInterface';

export default CallInterface;
